// LWES Community — Reddit-style posts + voting + threaded comments, backed by
// Firebase Firestore. Loaded as <script type="module"> only when the section
// is configured (see lwes-community.liquid).
//
// This module deliberately COPIES the proven helpers from lwes-academy-chat.js
// (Google sign-in, avatar/timestamp/status helpers, rate limiting, XSS-safe
// textContent-only rendering) instead of sharing a file, so the working
// landing-page chat is never at risk from changes here.
//
// Identity: posting, voting, and commenting require signing in with Google
// via Firebase Authentication (name + photo come from the Google account).
// Reading stays public. The "Academy Team" badge is automatic for Firebase
// uids listed in the section's admin_uids setting.
//
// Data model (all in the same Firebase project as the chat, new collection):
//   communityPosts/{postId}                      — the post
//   communityPosts/{postId}/votes/{uid}          — one vote doc per user
//   communityPosts/{postId}/comments/{commentId} — top-level comments
//   communityPosts/{postId}/comments/{commentId}/replies/{replyId}
//
// Voting runs in a Firestore transaction that reads the user's current vote,
// writes/updates/deletes it, and adjusts the post's score by the delta.
// Security model: every writer is signed in with Firebase Auth, so the rules
// verify request.auth (the stored `uid` must match the caller, votes are keyed
// by uid, and you can only delete your own content) on top of the shape and
// score/commentCount delta checks; moderation is manual via the Firebase
// console. See community-setup.md.

const FIREBASE_SDK_VERSION = '10.12.2';

const POST_RATE_LIMIT_MS = 30000;
const COMMENT_RATE_LIMIT_MS = 9000;
const TITLE_MAX = 150;
const BODY_MAX = 5000;
const COMMENT_MAX = 500;
const FEED_LIMIT = 50;
const SNIPPET_MAX = 220;

const STORAGE_KEYS = {
  lastPost: 'lwesCommunityLastPost',
  lastComment: 'lwesCommunityLastComment',
};

document.querySelectorAll('[data-lwes-community]').forEach((root) => {
  init(root);
});

async function init(root) {
  const config = readConfig(root);

  const state = {
    root,
    config,
    refs: {
      feed: root.querySelector('[data-lc-feed]'),
      empty: root.querySelector('[data-lc-empty]'),
      tabs: Array.from(root.querySelectorAll('[data-lc-sort]')),
      chips: Array.from(root.querySelectorAll('[data-lc-category]')),
      openComposer: root.querySelector('[data-lc-open-composer]'),
      composerModal: root.querySelector('[data-lc-composer-modal]'),
      postForm: root.querySelector('[data-lc-post-form]'),
      title: root.querySelector('[data-lc-title]'),
      body: root.querySelector('[data-lc-body]'),
      categorySelect: root.querySelector('[data-lc-category-select]'),
      imageInput: root.querySelector('[data-lc-image]'),
      imagePreview: root.querySelector('[data-lc-image-preview]'),
      imagePreviewImg: root.querySelector('[data-lc-image-preview-img]'),
      imageRemove: root.querySelector('[data-lc-image-remove]'),
      postStatus: root.querySelector('[data-lc-post-status]'),
      detailModal: root.querySelector('[data-lc-detail-modal]'),
      detailPost: root.querySelector('[data-lc-detail-post]'),
      commentsHeading: root.querySelector('[data-lc-comments-heading]'),
      commentsList: root.querySelector('[data-lc-comments-list]'),
      commentForm: root.querySelector('[data-lc-comment-form]'),
      commentInput: root.querySelector('[data-lc-comment-input]'),
      commentStatus: root.querySelector('[data-lc-comment-status]'),
    },
    db: null,
    firestore: null,
    auth: null,
    authMod: null,
    currentUser: null,
    emailAuthInFlight: false,
    sort: 'hot',
    category: '',
    posts: [], // [{ id, data }] as loaded from the active sort's query
    unsubscribeFeed: null,
    myVotes: new Map(), // postId -> 1 | -1
    fetchedVoteIds: new Set(),
    pendingImageFile: null,
    pendingImageUrl: null, // object URL for the local preview
    detail: {
      postId: null,
      data: null,
      unsubscribePost: null,
      unsubscribeComments: null,
      comments: new Map(), // commentId -> { expanded, replies, unsubscribe }
    },
  };

  try {
    const { initializeApp } = await import(
      `https://www.gstatic.com/firebasejs/${FIREBASE_SDK_VERSION}/firebase-app.js`
    );
    state.firestore = await import(
      `https://www.gstatic.com/firebasejs/${FIREBASE_SDK_VERSION}/firebase-firestore.js`
    );
    state.authMod = await import(
      `https://www.gstatic.com/firebasejs/${FIREBASE_SDK_VERSION}/firebase-auth.js`
    );
    const app = initializeApp(config.firebase);
    state.db = state.firestore.getFirestore(app);
    state.auth = state.authMod.getAuth(app);
  } catch (err) {
    showFeedError(state, 'The community is temporarily unavailable. Please try again later.');
    console.error('[lwes-community] Firebase init failed', err);
    return;
  }

  setupTabs(state);
  setupChips(state);
  setupComposerModal(state);
  setupDetailModal(state);
  setupPostForm(state);
  setupCommentForm(state);
  setupAuthUI(state);
  updateAuthUI(state);
  subscribeFeed(state);

  // Firebase Auth restores any existing session and notifies us on sign-in /
  // sign-out — the single source of truth for currentUser.
  state.authMod.onAuthStateChanged(state.auth, (user) => setCurrentUser(state, user));
}

function setCurrentUser(state, user) {
  if (requiresEmailVerification(user)) {
    state.currentUser = null;
    if (!state.emailAuthInFlight) {
      state.authMod.signOut(state.auth).catch((err) => {
        console.error('[lwes-community] unverified email sign-out failed', err);
      });
    }
  } else {
    state.currentUser = user
      ? { uid: user.uid, name: user.displayName || 'Member', photoURL: user.photoURL || '' }
      : null;
  }
  onAuthChanged(state);
}

function readConfig(root) {
  const d = root.dataset;
  return {
    firebase: {
      apiKey: d.firebaseApiKey,
      authDomain: d.firebaseAuthDomain,
      projectId: d.firebaseProjectId,
      storageBucket: d.firebaseStorageBucket,
      messagingSenderId: d.firebaseMessagingSenderId,
      appId: d.firebaseAppId,
    },
    adminUids: parseIdList(d.adminUids),
    imgbbApiKey: d.imgbbApiKey || '',
    maxImageMb: Number(d.maxImageMb) || 8,
    emptyMessage: d.emptyMessage || 'No posts yet — be the first to share!',
    loginLabel: d.loginLabel || 'Continue with Google',
    logoutLabel: d.logoutLabel || 'Log out',
    replyLabel: d.replyLabel || 'Reply',
    repliesLabel: d.repliesLabel || 'replies',
    replyPlaceholder: d.replyPlaceholder || 'Write a reply...',
    commentsLabel: d.commentsLabel || 'comments',
    sendLabel: d.sendLabel || 'Send',
    deleteLabel: d.deleteLabel || 'Delete',
    addPhotoLabel: d.addPhotoLabel || 'Add photo',
    removePhotoLabel: d.removePhotoLabel || 'Remove photo',
  };
}

function parseIdList(raw) {
  if (!raw) return new Set();
  return new Set(
    raw
      .split(/[\n,]/)
      .map((id) => id.trim())
      .filter(Boolean)
  );
}

// ---- Sign-in via Firebase Auth: Google, email/password (copied from
// the chat) ---- onAuthStateChanged (set up in init) is the single source of
// truth; the only exception is email *sign-up*, which pushes the freshly set
// display name immediately (see buildEmailAuthForm).

function setupAuthUI(state) {
  state.root.querySelectorAll('[data-lc-authui]').forEach((el) => {
    renderAuthPanel(state, el, false);
  });
}

function renderAuthPanel(state, container, compact) {
  container.replaceChildren();

  const providers = document.createElement('div');
  providers.className = 'lc-providers';
  providers.append(
    buildProviderButton('google', state.config.loginLabel, () => signInWithPopupProvider(state, new state.authMod.GoogleAuthProvider()))
  );
  container.appendChild(providers);

  if (!compact) {
    container.appendChild(buildDivider());
    container.appendChild(buildEmailAuthForm(state));
  }
}

function signInWithPopupProvider(state, provider) {
  state.authMod.signInWithPopup(state.auth, provider).catch((err) => {
    if (err && (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request')) {
      return;
    }
    console.error('[lwes-community] sign-in failed', err);
    window.alert('Could not sign in. Please try again.');
  });
}

function handleLogout(state) {
  state.authMod.signOut(state.auth).catch((err) => {
    console.error('[lwes-community] sign-out failed', err);
  });
}

function buildProviderButton(kind, label, onClick) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = `lc-provider-btn lc-provider-btn--${kind}`;
  btn.appendChild(providerIcon(kind));
  const span = document.createElement('span');
  span.textContent = label;
  btn.appendChild(span);
  btn.addEventListener('click', onClick);
  return btn;
}

function buildDivider() {
  const div = document.createElement('div');
  div.className = 'lc-or';
  const span = document.createElement('span');
  span.textContent = 'or';
  div.appendChild(span);
  return div;
}

// Email/password sign-in with an inline toggle to account creation. New
// accounts collect a display name so the community never shows "Member".
function buildEmailAuthForm(state) {
  const form = document.createElement('form');
  form.className = 'lc-email-form';
  form.noValidate = true;

  let mode = 'signin';

  const name = buildField('text', 'Your name', 'name');
  name.wrap.hidden = true;
  const email = buildField('email', 'Email', 'email');
  const password = buildField('password', 'Password', 'current-password');

  const submit = document.createElement('button');
  submit.type = 'submit';
  submit.className = 'lc-btn lc-btn--green lc-email-submit';
  submit.textContent = 'Sign in';

  const status = document.createElement('p');
  status.className = 'lc-status';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');

  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'lc-login-link';
  toggle.textContent = 'New here? Create an account';
  toggle.addEventListener('click', () => {
    mode = mode === 'signin' ? 'signup' : 'signin';
    name.wrap.hidden = mode === 'signin';
    password.el.autocomplete = mode === 'signin' ? 'current-password' : 'new-password';
    submit.textContent = mode === 'signin' ? 'Sign in' : 'Create account';
    toggle.textContent = mode === 'signin' ? 'New here? Create an account' : 'Have an account? Sign in';
    setStatus(status, '', null);
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    setStatus(status, '', null);

    const emailVal = email.el.value.trim();
    const passVal = password.el.value;
    if (!emailVal || !passVal) {
      setStatus(status, 'Enter your email and password.', 'error');
      return;
    }

    setFormDisabled(form, true);
    state.emailAuthInFlight = true;
    try {
      if (mode === 'signup') {
        const nameVal = name.el.value.trim();
        if (!nameVal) {
          setStatus(status, 'Please enter your name.', 'error');
          return;
        }
        const cred = await state.authMod.createUserWithEmailAndPassword(state.auth, emailVal, passVal);
        await state.authMod.updateProfile(cred.user, { displayName: nameVal });
        await sendEmailVerificationAndSignOut(state, cred.user);
        setStatus(status, 'Account created. Check your email to verify it, then sign in.', 'success');
      } else {
        const cred = await state.authMod.signInWithEmailAndPassword(state.auth, emailVal, passVal);
        if (requiresEmailVerification(cred.user)) {
          await sendEmailVerificationAndSignOut(state, cred.user);
          setStatus(status, 'Please verify your email first. We sent a new verification email.', 'error');
          return;
        }
      }
    } catch (err) {
      setStatus(status, emailAuthError(err), 'error');
    } finally {
      state.emailAuthInFlight = false;
      if (requiresEmailVerification(state.auth.currentUser)) {
        state.authMod.signOut(state.auth).catch((err) => {
          console.error('[lwes-community] unverified email sign-out failed', err);
        });
      }
      setFormDisabled(form, false);
    }
  });

  form.append(name.wrap, email.wrap, password.wrap, submit, status, toggle);
  return form;
}

async function sendEmailVerificationAndSignOut(state, user) {
  await state.authMod.sendEmailVerification(user);
  await state.authMod.signOut(state.auth);
}

function requiresEmailVerification(user) {
  return Boolean(user && isEmailPasswordUser(user) && !user.emailVerified);
}

function isEmailPasswordUser(user) {
  return Array.isArray(user.providerData) && user.providerData.some((provider) => provider.providerId === 'password');
}

function buildField(type, label, autocomplete) {
  const wrap = document.createElement('label');
  wrap.className = 'lc-field';
  const span = document.createElement('span');
  span.className = 'lc-field__label';
  span.textContent = label;
  const el = document.createElement('input');
  el.type = type;
  el.className = 'lc-email-input';
  el.placeholder = label;
  if (autocomplete) el.autocomplete = autocomplete;
  wrap.append(span, el);
  return { wrap, el };
}

function emailAuthError(err) {
  const code = err && err.code;
  switch (code) {
    case 'auth/invalid-email':
      return 'That email address looks invalid.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters.';
    case 'auth/email-already-in-use':
      return 'That email already has an account — try signing in instead.';
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-credential':
      return 'Email or password is incorrect.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a moment and try again.';
    default:
      return 'Could not sign in. Please try again.';
  }
}

function providerIcon(kind) {
  const NS = 'http://www.w3.org/2000/svg';
  const make = (viewBox, paths) => {
    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('viewBox', viewBox);
    svg.setAttribute('aria-hidden', 'true');
    paths.forEach(([d, fill]) => {
      const path = document.createElementNS(NS, 'path');
      path.setAttribute('d', d);
      if (fill) path.setAttribute('fill', fill);
      svg.appendChild(path);
    });
    return svg;
  };
  return make('0 0 48 48', [
    ['M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z', '#FFC107'],
    ['M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z', '#FF3D00'],
    ['M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z', '#4CAF50'],
    ['M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z', '#1976D2'],
  ]);
}

function onAuthChanged(state) {
  state.myVotes.clear();
  state.fetchedVoteIds.clear();
  updateAuthUI(state);
  renderFeed(state);
  if (state.currentUser) fetchMyVotes(state);
  if (state.detail.postId) renderDetailComments(state);
}


// Toggles every static login prompt / auth-gated form in the section and
// fills the "Posting as {name}" bars. (JS-built reply composers handle
// their own state when rendered.)
function updateAuthUI(state) {
  const loggedIn = Boolean(state.currentUser);
  state.root.querySelectorAll('[data-lc-login]').forEach((el) => {
    el.hidden = loggedIn;
  });
  state.root.querySelectorAll('[data-lc-auth-form]').forEach((el) => {
    el.hidden = !loggedIn;
  });
  state.root.querySelectorAll('[data-lc-poster]').forEach((el) => {
    renderPoster(state, el);
  });
}

function renderPoster(state, posterEl) {
  posterEl.replaceChildren();
  if (!state.currentUser) return;

  const avatar = createAvatar(state.currentUser.photoURL, state.currentUser.name, 'lc-avatar--sm');

  const name = document.createElement('span');
  name.className = 'lc-poster__name';
  name.textContent = state.currentUser.name;

  const logout = document.createElement('button');
  logout.type = 'button';
  logout.className = 'lc-poster__logout';
  logout.textContent = state.config.logoutLabel;
  logout.addEventListener('click', () => handleLogout(state));

  posterEl.append(avatar, name, logout);
}

// ---- Feed: sort tabs, category chips, real-time listener ----

function setupTabs(state) {
  state.refs.tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const sort = tab.dataset.lcSort;
      if (sort === state.sort) return;
      state.sort = sort;
      state.refs.tabs.forEach((t) => {
        const active = t === tab;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      subscribeFeed(state);
    });
  });
}

function setupChips(state) {
  state.refs.chips.forEach((chip) => {
    chip.addEventListener('click', () => {
      state.category = chip.dataset.lcCategory || '';
      state.refs.chips.forEach((ch) => ch.classList.toggle('is-active', ch === chip));
      renderFeed(state);
    });
  });
}

function subscribeFeed(state) {
  const { collection, query, orderBy, limit, onSnapshot } = state.firestore;

  if (state.unsubscribeFeed) {
    state.unsubscribeFeed();
    state.unsubscribeFeed = null;
  }

  // "Hot" loads the recent window by timestamp and ranks client-side
  // (score + recency decay) — no composite index needed.
  const orderField = state.sort === 'top' ? 'score' : 'timestamp';
  const q = query(collection(state.db, 'communityPosts'), orderBy(orderField, 'desc'), limit(FEED_LIMIT));

  state.unsubscribeFeed = onSnapshot(
    q,
    (snapshot) => {
      const docs = [];
      snapshot.forEach((doc) => docs.push({ id: doc.id, data: doc.data() }));
      state.posts = docs;
      renderFeed(state);
      if (state.currentUser) fetchMyVotes(state);
      syncDetailFromFeed(state);
    },
    (err) => {
      showFeedError(state, 'Unable to load posts right now.');
      console.error('[lwes-community] feed onSnapshot error', err);
    }
  );
}

function sortedFilteredPosts(state) {
  let posts = state.posts;
  if (state.category) {
    posts = posts.filter(({ data }) => (data.category || '') === state.category);
  }
  if (state.sort === 'hot') {
    posts = posts.slice().sort((a, b) => hotScore(b.data) - hotScore(a.data));
  }
  return posts;
}

function hotScore(post) {
  const ms = post.timestamp && typeof post.timestamp.toDate === 'function' ? post.timestamp.toDate().getTime() : Date.now();
  const ageHours = Math.max(0, (Date.now() - ms) / 3600000);
  return (post.score || 0) / Math.pow(ageHours + 2, 1.5);
}

// Fetch this user's vote doc for any loaded post we haven't checked yet, so
// the arrows can show their current vote. One small read per new post.
async function fetchMyVotes(state) {
  const { doc, getDoc } = state.firestore;
  const uid = state.currentUser && state.currentUser.uid;
  if (!uid) return;

  const unknown = state.posts.filter(({ id }) => !state.fetchedVoteIds.has(id));
  if (unknown.length === 0) return;
  unknown.forEach(({ id }) => state.fetchedVoteIds.add(id));

  await Promise.all(
    unknown.map(async ({ id }) => {
      try {
        const snap = await getDoc(doc(state.db, 'communityPosts', id, 'votes', uid));
        if (snap.exists()) state.myVotes.set(id, snap.data().value);
      } catch (err) {
        state.fetchedVoteIds.delete(id);
        console.error('[lwes-community] vote lookup failed', err);
      }
    })
  );
  renderFeed(state);
  if (state.detail.postId) renderDetailPost(state);
}

// ---- Feed rendering (XSS-safe: textContent only, never innerHTML) ----

function renderFeed(state) {
  const { feed, empty } = state.refs;
  feed.querySelectorAll('.lc-post, .lc-feed__error').forEach((el) => el.remove());

  const posts = sortedFilteredPosts(state);
  if (posts.length === 0) {
    if (empty) {
      empty.hidden = false;
      empty.textContent = state.config.emptyMessage;
    }
    return;
  }
  if (empty) empty.hidden = true;

  posts.forEach(({ id, data }) => {
    feed.appendChild(renderPostCard(state, id, data));
  });
}

function renderPostCard(state, id, post) {
  const card = document.createElement('article');
  card.className = 'lc-post';
  if (isAdminAuthor(state, post)) card.classList.add('lc-post--admin');

  card.appendChild(buildVoteColumn(state, id, post));

  const main = document.createElement('div');
  main.className = 'lc-post__main';

  main.appendChild(buildPostMeta(state, post));

  const title = document.createElement('h2');
  title.className = 'lc-post__title';
  title.textContent = post.title || '';
  main.appendChild(title);

  if (post.body) {
    const snippet = document.createElement('p');
    snippet.className = 'lc-post__snippet';
    snippet.textContent = post.body.length > SNIPPET_MAX ? `${post.body.slice(0, SNIPPET_MAX).trimEnd()}…` : post.body;
    main.appendChild(snippet);
  }

  const imageUrl = safeUrl(post.imageURL);
  if (imageUrl) {
    const thumbWrap = document.createElement('div');
    thumbWrap.className = 'lc-post__thumb';
    const img = document.createElement('img');
    img.src = imageUrl;
    img.alt = post.title || '';
    img.loading = 'lazy';
    img.referrerPolicy = 'no-referrer';
    thumbWrap.appendChild(img);
    main.appendChild(thumbWrap);
  }

  const footer = document.createElement('div');
  footer.className = 'lc-post__footer';
  const comments = document.createElement('button');
  comments.type = 'button';
  comments.className = 'lc-post__comments';
  comments.append(createCommentIcon());
  const commentsText = document.createElement('span');
  commentsText.textContent = `${post.commentCount || 0} ${state.config.commentsLabel}`;
  comments.appendChild(commentsText);
  footer.appendChild(comments);
  if (isOwnMessage(state, post)) {
    footer.appendChild(buildDeleteButton(state, 'lc-post__delete', () => deletePost(state, id, post)));
  }
  main.appendChild(footer);

  card.appendChild(main);

  // The whole card opens the detail view, except interactive children
  // (vote arrows handle their own clicks and stop propagation).
  card.addEventListener('click', () => openDetail(state, id));
  card.tabIndex = 0;
  card.setAttribute('role', 'button');
  card.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && e.target === card) openDetail(state, id);
  });

  return card;
}

function buildPostMeta(state, post) {
  const meta = document.createElement('div');
  meta.className = 'lc-post__meta';

  if (post.category) {
    const pill = document.createElement('span');
    pill.className = 'lc-post__category';
    pill.textContent = post.category;
    meta.appendChild(pill);
  }

  const avatar = createAvatar(post.photoURL, post.name, 'lc-avatar--xs');
  meta.appendChild(avatar);

  const author = document.createElement('span');
  author.className = 'lc-post__author';
  author.textContent = post.name || 'Anonymous';
  meta.appendChild(author);

  if (isAdminAuthor(state, post)) meta.appendChild(createBadge());

  const time = document.createElement('span');
  time.className = 'lc-post__time';
  time.textContent = formatRelative(post.timestamp);
  meta.appendChild(time);

  return meta;
}

function buildVoteColumn(state, id, post) {
  const col = document.createElement('div');
  col.className = 'lc-vote';

  const myVote = state.myVotes.get(id) || 0;

  const up = document.createElement('button');
  up.type = 'button';
  up.className = 'lc-vote__btn lc-vote__btn--up';
  up.setAttribute('aria-label', 'Upvote');
  up.textContent = '▲';
  if (myVote === 1) up.classList.add('is-active');

  const score = document.createElement('span');
  score.className = 'lc-vote__score';
  score.textContent = String(post.score || 0);
  if (myVote === 1) score.classList.add('is-up');
  if (myVote === -1) score.classList.add('is-down');

  const down = document.createElement('button');
  down.type = 'button';
  down.className = 'lc-vote__btn lc-vote__btn--down';
  down.setAttribute('aria-label', 'Downvote');
  down.textContent = '▼';
  if (myVote === -1) down.classList.add('is-active');

  up.addEventListener('click', (e) => {
    e.stopPropagation();
    castVote(state, id, 1);
  });
  down.addEventListener('click', (e) => {
    e.stopPropagation();
    castVote(state, id, -1);
  });

  col.append(up, score, down);
  return col;
}

function createBadge() {
  const badge = document.createElement('span');
  badge.className = 'lc-badge';
  badge.textContent = 'Academy Team';
  return badge;
}

function createAvatar(photoURL, name, extraClass) {
  const avatar = document.createElement('div');
  avatar.className = extraClass ? `lc-avatar ${extraClass}` : 'lc-avatar';
  const url = safeUrl(photoURL);
  if (url) {
    const img = document.createElement('img');
    img.src = url;
    img.alt = '';
    img.loading = 'lazy';
    img.referrerPolicy = 'no-referrer';
    avatar.appendChild(img);
  } else {
    avatar.textContent = initials(name);
  }
  return avatar;
}

function createCommentIcon() {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', 'M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.8-.9L3 21l1.9-5.7a8.5 8.5 0 1 1 16.1-3.8z');
  svg.appendChild(path);
  return svg;
}

function initials(name) {
  return (name || 'NP').trim().slice(0, 2).toUpperCase();
}

function formatTimestamp(ts) {
  if (!ts || typeof ts.toDate !== 'function') return 'Just now';
  return ts.toDate().toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatRelative(ts) {
  if (!ts || typeof ts.toDate !== 'function') return 'Just now';
  const seconds = Math.floor((Date.now() - ts.toDate().getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return ts.toDate().toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Only allow http(s) URLs from Firestore data into src attributes.
function safeUrl(url) {
  if (typeof url !== 'string' || !/^https?:\/\//i.test(url)) return '';
  return url;
}

function showFeedError(state, msg) {
  const { feed, empty } = state.refs;
  if (empty) empty.hidden = true;
  let errorEl = feed.querySelector('.lc-feed__error');
  if (!errorEl) {
    errorEl = document.createElement('p');
    errorEl.className = 'lc-feed__error';
    feed.appendChild(errorEl);
  }
  errorEl.textContent = msg;
}

// ---- Voting (Firestore transaction; optimistic UI, snapshot reconciles) ----

async function castVote(state, postId, value) {
  if (!state.currentUser) {
    openComposerModal(state); // shows the Google sign-in prompt
    return;
  }

  const uid = state.currentUser.uid;
  const prev = state.myVotes.get(postId) || 0;
  const next = prev === value ? 0 : value;
  const delta = next - prev;
  if (delta === 0) return;

  // Optimistic update — the feed onSnapshot reconciles the real score.
  if (next === 0) state.myVotes.delete(postId);
  else state.myVotes.set(postId, next);
  const entry = state.posts.find((p) => p.id === postId);
  if (entry) entry.data = { ...entry.data, score: (entry.data.score || 0) + delta };
  renderFeed(state);
  if (state.detail.postId === postId) renderDetailPost(state);

  const { doc, runTransaction, serverTimestamp, increment } = state.firestore;
  try {
    await runTransaction(state.db, async (tx) => {
      const voteRef = doc(state.db, 'communityPosts', postId, 'votes', uid);
      const postRef = doc(state.db, 'communityPosts', postId);
      const voteSnap = await tx.get(voteRef);
      const current = voteSnap.exists() ? voteSnap.data().value : 0;
      const target = current === value ? 0 : value;
      const change = target - current;
      if (change === 0) return;
      if (target === 0) {
        tx.delete(voteRef);
      } else {
        tx.set(voteRef, { value: target, timestamp: serverTimestamp() });
      }
      tx.update(postRef, { score: increment(change) });
    });
  } catch (err) {
    console.error('[lwes-community] vote failed', err);
    // Revert the optimistic vote; score reconciles via onSnapshot.
    if (prev === 0) state.myVotes.delete(postId);
    else state.myVotes.set(postId, prev);
    if (entry) entry.data = { ...entry.data, score: (entry.data.score || 0) - delta };
    renderFeed(state);
    if (state.detail.postId === postId) renderDetailPost(state);
  }
}

// ---- Deleting own content ----
// The UI only offers Delete on the current user's own documents. Like
// voting, this is trust-based (no Firebase Auth) — rules allow the delete,
// the page decides who sees the button. See community-setup.md.

function buildDeleteButton(state, className, onDelete) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = className;
  btn.textContent = state.config.deleteLabel;
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    onDelete();
  });
  return btn;
}

async function deletePost(state, postId, post) {
  if (!state.currentUser || !isOwnMessage(state, post)) return;
  if (!window.confirm('Delete this post? This cannot be undone.')) return;

  const { doc, deleteDoc } = state.firestore;
  try {
    if (state.detail.postId === postId) closeDetail(state);
    await deleteDoc(doc(state.db, 'communityPosts', postId));
    // Votes/comments under the post become unreachable; the feed snapshot
    // removes the card automatically.
  } catch (err) {
    console.error('[lwes-community] post delete failed', err);
    window.alert('Could not delete the post. Please try again.');
  }
}

async function deleteComment(state, commentId, msg) {
  const postId = state.detail.postId;
  if (!postId || !state.currentUser || !isOwnMessage(state, msg)) return;
  if (!window.confirm('Delete this comment? This cannot be undone.')) return;

  const { doc, deleteDoc, updateDoc, increment } = state.firestore;
  try {
    const entry = state.detail.comments.get(commentId);
    if (entry && entry.unsubscribe) entry.unsubscribe();
    state.detail.comments.delete(commentId);
    await deleteDoc(doc(state.db, 'communityPosts', postId, 'comments', commentId));
    // The comment's replies disappear with it, so the post's discussion
    // count drops by the comment plus its replies.
    const removed = 1 + (msg.replyCount || 0);
    updateDoc(doc(state.db, 'communityPosts', postId), { commentCount: increment(-removed) }).catch((err) => {
      console.error('[lwes-community] comment count update failed', err);
    });
  } catch (err) {
    console.error('[lwes-community] comment delete failed', err);
    window.alert('Could not delete the comment. Please try again.');
  }
}

async function deleteReply(state, commentId, replyId, msg) {
  const postId = state.detail.postId;
  if (!postId || !state.currentUser || !isOwnMessage(state, msg)) return;
  if (!window.confirm('Delete this reply? This cannot be undone.')) return;

  const { doc, deleteDoc, updateDoc, increment } = state.firestore;
  try {
    await deleteDoc(doc(state.db, 'communityPosts', postId, 'comments', commentId, 'replies', replyId));
    updateDoc(doc(state.db, 'communityPosts', postId, 'comments', commentId), { replyCount: increment(-1) }).catch((err) => {
      console.error('[lwes-community] reply count update failed', err);
    });
    updateDoc(doc(state.db, 'communityPosts', postId), { commentCount: increment(-1) }).catch((err) => {
      console.error('[lwes-community] comment count update failed', err);
    });
  } catch (err) {
    console.error('[lwes-community] reply delete failed', err);
    window.alert('Could not delete the reply. Please try again.');
  }
}

// ---- Create-post modal ----

function setupComposerModal(state) {
  const { openComposer, composerModal } = state.refs;
  openComposer.addEventListener('click', () => openComposerModal(state));
  composerModal.querySelectorAll('[data-lc-composer-close]').forEach((el) => {
    el.addEventListener('click', () => closeComposerModal(state));
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (!state.refs.composerModal.hidden) closeComposerModal(state);
    else if (!state.refs.detailModal.hidden) closeDetail(state);
  });
}

function openComposerModal(state) {
  state.refs.composerModal.hidden = false;
  syncBodyScroll(state);
  if (state.currentUser && state.refs.title) state.refs.title.focus();
}

function closeComposerModal(state) {
  state.refs.composerModal.hidden = true;
  syncBodyScroll(state);
}

function syncBodyScroll(state) {
  const anyOpen = !state.refs.composerModal.hidden || !state.refs.detailModal.hidden;
  document.body.style.overflow = anyOpen ? 'hidden' : '';
}

function setupPostForm(state) {
  const { postForm, imageInput, imagePreview, imagePreviewImg, imageRemove, postStatus } = state.refs;

  if (imageInput) {
    imageInput.addEventListener('change', () => {
      const file = imageInput.files && imageInput.files[0];
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        setStatus(postStatus, 'Please choose an image file.', 'error');
        imageInput.value = '';
        return;
      }
      if (file.size > state.config.maxImageMb * 1024 * 1024) {
        setStatus(postStatus, `Photo is too large — max ${state.config.maxImageMb} MB.`, 'error');
        imageInput.value = '';
        return;
      }
      setStatus(postStatus, '', null);
      clearPendingImage(state, false);
      state.pendingImageFile = file;
      state.pendingImageUrl = URL.createObjectURL(file);
      imagePreviewImg.src = state.pendingImageUrl;
      imagePreview.hidden = false;
    });
  }

  if (imageRemove) {
    imageRemove.addEventListener('click', () => clearPendingImage(state, true));
  }

  postForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    setStatus(postStatus, '', null);

    if (!state.currentUser) {
      setStatus(postStatus, 'Please sign in first.', 'error');
      return;
    }

    const title = (state.refs.title.value || '').trim().slice(0, TITLE_MAX);
    const body = (state.refs.body.value || '').trim().slice(0, BODY_MAX);
    const category = state.refs.categorySelect ? state.refs.categorySelect.value : '';

    if (!title) {
      setStatus(postStatus, 'Please give your post a title.', 'error');
      return;
    }

    const remaining = rateLimitRemaining(STORAGE_KEYS.lastPost, POST_RATE_LIMIT_MS);
    if (remaining > 0) {
      setStatus(postStatus, `Please wait ${remaining}s before posting again.`, 'error');
      return;
    }

    setFormDisabled(postForm, true);
    try {
      let imageURL = '';
      if (state.pendingImageFile) {
        setStatus(postStatus, 'Uploading photo…', null);
        imageURL = await uploadToImgbb(state.config.imgbbApiKey, state.pendingImageFile);
      }

      setStatus(postStatus, 'Posting…', null);
      const { collection, addDoc, serverTimestamp } = state.firestore;
      const payload = {
        title,
        name: state.currentUser.name,
        photoURL: state.currentUser.photoURL,
        uid: state.currentUser.uid,
        score: 0,
        commentCount: 0,
        timestamp: serverTimestamp(),
      };
      if (body) payload.body = body;
      if (category) payload.category = category;
      if (imageURL) payload.imageURL = imageURL.slice(0, 1000);

      await addDoc(collection(state.db, 'communityPosts'), payload);

      localStorage.setItem(STORAGE_KEYS.lastPost, String(Date.now()));
      postForm.reset();
      clearPendingImage(state, true);
      setStatus(postStatus, '', null);
      closeComposerModal(state);
      // Jump to "New" so the author immediately sees their post on top.
      const newTab = state.refs.tabs.find((t) => t.dataset.lcSort === 'new');
      if (newTab && state.sort !== 'new') newTab.click();
    } catch (err) {
      console.error('[lwes-community] post failed', err);
      setStatus(postStatus, 'Could not publish your post. Please try again.', 'error');
    } finally {
      setFormDisabled(postForm, false);
    }
  });
}

function clearPendingImage(state, resetInput) {
  if (state.pendingImageUrl) URL.revokeObjectURL(state.pendingImageUrl);
  state.pendingImageFile = null;
  state.pendingImageUrl = null;
  if (state.refs.imagePreviewImg) state.refs.imagePreviewImg.removeAttribute('src');
  if (state.refs.imagePreview) state.refs.imagePreview.hidden = true;
  if (resetInput && state.refs.imageInput) state.refs.imageInput.value = '';
}

async function uploadToImgbb(apiKey, file) {
  if (!apiKey) throw new Error('imgbb API key is not configured');
  const formData = new FormData();
  formData.append('image', file);
  const res = await fetch(`https://api.imgbb.com/1/upload?key=${encodeURIComponent(apiKey)}`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) throw new Error(`imgbb upload failed (${res.status})`);
  const json = await res.json();
  const url = json && json.data && json.data.url;
  if (!url) throw new Error('imgbb upload returned no URL');
  return url;
}

// ---- Post detail (overlay with full post + threaded comments) ----

function setupDetailModal(state) {
  state.refs.detailModal.querySelectorAll('[data-lc-detail-close]').forEach((el) => {
    el.addEventListener('click', () => closeDetail(state));
  });
}

function openDetail(state, postId) {
  closeDetailSubscriptions(state);
  state.detail.postId = postId;

  const entry = state.posts.find((p) => p.id === postId);
  state.detail.data = entry ? entry.data : null;
  renderDetailPost(state);

  state.refs.commentsList.replaceChildren();
  state.refs.detailModal.hidden = false;
  syncBodyScroll(state);

  const { doc, collection, query, orderBy, onSnapshot } = state.firestore;

  // Live post doc (score / comment count keep updating while open).
  state.detail.unsubscribePost = onSnapshot(
    doc(state.db, 'communityPosts', postId),
    (snap) => {
      if (!snap.exists()) return;
      state.detail.data = snap.data();
      renderDetailPost(state);
    },
    (err) => console.error('[lwes-community] post onSnapshot error', err)
  );

  const q = query(collection(state.db, 'communityPosts', postId, 'comments'), orderBy('timestamp', 'asc'));
  state.detail.unsubscribeComments = onSnapshot(
    q,
    (snapshot) => {
      const docs = [];
      snapshot.forEach((d) => docs.push({ id: d.id, data: d.data() }));
      state.detail.commentDocs = docs;
      renderDetailComments(state);
    },
    (err) => console.error('[lwes-community] comments onSnapshot error', err)
  );
}

function closeDetail(state) {
  closeDetailSubscriptions(state);
  state.detail.postId = null;
  state.detail.data = null;
  state.detail.commentDocs = [];
  state.refs.detailModal.hidden = true;
  syncBodyScroll(state);
}

function closeDetailSubscriptions(state) {
  if (state.detail.unsubscribePost) state.detail.unsubscribePost();
  if (state.detail.unsubscribeComments) state.detail.unsubscribeComments();
  state.detail.unsubscribePost = null;
  state.detail.unsubscribeComments = null;
  state.detail.comments.forEach((entry) => {
    if (entry.unsubscribe) entry.unsubscribe();
  });
  state.detail.comments.clear();
}

// Keep the open detail view in sync when its post changes in the feed window.
function syncDetailFromFeed(state) {
  if (!state.detail.postId || state.detail.unsubscribePost) return;
  const entry = state.posts.find((p) => p.id === state.detail.postId);
  if (entry) {
    state.detail.data = entry.data;
    renderDetailPost(state);
  }
}

function renderDetailPost(state) {
  const { detailPost, commentsHeading } = state.refs;
  const post = state.detail.data;
  detailPost.replaceChildren();
  if (!post) return;

  const wrap = document.createElement('div');
  wrap.className = 'lc-detail__post lc-post lc-post--detail';
  if (isAdminAuthor(state, post)) wrap.classList.add('lc-post--admin');

  wrap.appendChild(buildVoteColumn(state, state.detail.postId, post));

  const main = document.createElement('div');
  main.className = 'lc-post__main';

  main.appendChild(buildPostMeta(state, post));

  const title = document.createElement('h2');
  title.className = 'lc-post__title lc-post__title--detail';
  title.textContent = post.title || '';
  main.appendChild(title);

  if (post.body) {
    const body = document.createElement('p');
    body.className = 'lc-post__body';
    body.textContent = post.body;
    main.appendChild(body);
  }

  const imageUrl = safeUrl(post.imageURL);
  if (imageUrl) {
    const figure = document.createElement('div');
    figure.className = 'lc-post__image';
    const img = document.createElement('img');
    img.src = imageUrl;
    img.alt = post.title || '';
    img.loading = 'lazy';
    img.referrerPolicy = 'no-referrer';
    figure.appendChild(img);
    main.appendChild(figure);
  }

  if (isOwnMessage(state, post)) {
    const footer = document.createElement('div');
    footer.className = 'lc-post__footer';
    footer.appendChild(buildDeleteButton(state, 'lc-post__delete', () => deletePost(state, state.detail.postId, post)));
    main.appendChild(footer);
  }

  wrap.appendChild(main);
  detailPost.appendChild(wrap);

  if (commentsHeading) {
    const count = post.commentCount || 0;
    commentsHeading.textContent = `${count} ${state.config.commentsLabel}`;
  }
}

// ---- Comments + threaded replies (adapted from the chat's threading) ----

function renderDetailComments(state) {
  const list = state.refs.commentsList;
  if (!list) return;
  list.replaceChildren();
  (state.detail.commentDocs || []).forEach(({ id, data }) => {
    list.appendChild(renderComment(state, id, data));
  });
}

// Optional photo attached to a comment or reply (same imgbb-hosted URL model
// as posts). Returns null when there's no valid image.
function buildMessageImage(msg) {
  const url = safeUrl(msg.imageURL);
  if (!url) return null;
  const figure = document.createElement('div');
  figure.className = 'lc-comment__image';
  const img = document.createElement('img');
  img.src = url;
  img.alt = '';
  img.loading = 'lazy';
  img.referrerPolicy = 'no-referrer';
  figure.appendChild(img);
  return figure;
}

function renderComment(state, commentId, msg) {
  const wrap = document.createElement('div');
  wrap.className = 'lc-comment';
  if (isAdminAuthor(state, msg)) wrap.classList.add('lc-comment--admin');
  if (isOwnMessage(state, msg)) wrap.classList.add('lc-comment--own');

  const avatar = createAvatar(msg.photoURL, msg.name, 'lc-avatar--sm');

  const bubble = document.createElement('div');
  bubble.className = 'lc-comment__bubble';

  const authorLine = document.createElement('div');
  const author = document.createElement('span');
  author.className = 'lc-comment__author';
  author.textContent = msg.name || 'Anonymous';
  authorLine.appendChild(author);
  if (isAdminAuthor(state, msg)) authorLine.appendChild(createBadge());

  const text = document.createElement('p');
  text.className = 'lc-comment__text';
  text.textContent = msg.message || '';

  const meta = document.createElement('div');
  meta.className = 'lc-comment__meta';

  const time = document.createElement('span');
  time.className = 'lc-comment__time';
  time.textContent = formatTimestamp(msg.timestamp);
  meta.appendChild(time);

  const replyToggle = document.createElement('button');
  replyToggle.type = 'button';
  replyToggle.className = 'lc-comment__reply-toggle';
  replyToggle.textContent = state.config.replyLabel;
  meta.appendChild(replyToggle);

  const replyCount = document.createElement('span');
  replyCount.className = 'lc-comment__reply-count';
  const count = msg.replyCount || 0;
  replyCount.textContent = count > 0 ? `${count} ${state.config.repliesLabel}` : '';
  replyCount.hidden = count === 0;
  meta.appendChild(replyCount);

  if (isOwnMessage(state, msg)) {
    meta.appendChild(buildDeleteButton(state, 'lc-comment__delete', () => deleteComment(state, commentId, msg)));
  }

  bubble.append(authorLine, text);
  const image = buildMessageImage(msg);
  if (image) bubble.appendChild(image);
  bubble.appendChild(meta);

  const repliesEl = document.createElement('div');
  repliesEl.className = 'lc-comment__replies';
  repliesEl.hidden = true;

  const repliesList = document.createElement('div');
  repliesList.className = 'lc-comment__replies-list';
  repliesEl.appendChild(repliesList);
  repliesEl.appendChild(buildReplyComposer(state, commentId));

  bubble.appendChild(repliesEl);
  wrap.append(avatar, bubble);

  replyToggle.addEventListener('click', () => {
    const expanded = toggleReplies(state, commentId, repliesEl, repliesList);
    replyToggle.textContent = expanded ? 'Hide' : state.config.replyLabel;
  });

  // Restore expanded state across re-renders.
  const existing = state.detail.comments.get(commentId);
  if (existing && existing.expanded) {
    repliesEl.hidden = false;
    replyToggle.textContent = 'Hide';
    renderReplies(state, commentId, repliesList, existing.replies);
  }

  return wrap;
}

function renderReply(state, commentId, replyId, msg) {
  const wrap = document.createElement('div');
  wrap.className = 'lc-comment lc-comment--reply';
  if (isAdminAuthor(state, msg)) wrap.classList.add('lc-comment--admin');
  if (isOwnMessage(state, msg)) wrap.classList.add('lc-comment--own');

  const avatar = createAvatar(msg.photoURL, msg.name, 'lc-avatar--xs');

  const bubble = document.createElement('div');
  bubble.className = 'lc-comment__bubble lc-comment__bubble--sm';

  const authorLine = document.createElement('div');
  const author = document.createElement('span');
  author.className = 'lc-comment__author';
  author.textContent = msg.name || 'Anonymous';
  authorLine.appendChild(author);
  if (isAdminAuthor(state, msg)) authorLine.appendChild(createBadge());

  const text = document.createElement('p');
  text.className = 'lc-comment__text';
  text.textContent = msg.message || '';

  const meta = document.createElement('div');
  meta.className = 'lc-comment__meta lc-comment__meta--reply';

  const time = document.createElement('span');
  time.className = 'lc-comment__time';
  time.textContent = formatTimestamp(msg.timestamp);
  meta.appendChild(time);

  if (isOwnMessage(state, msg)) {
    meta.appendChild(buildDeleteButton(state, 'lc-comment__delete', () => deleteReply(state, commentId, replyId, msg)));
  }

  bubble.append(authorLine, text);
  const image = buildMessageImage(msg);
  if (image) bubble.appendChild(image);
  bubble.appendChild(meta);
  wrap.append(avatar, bubble);
  return wrap;
}

function renderReplies(state, commentId, repliesList, replies) {
  repliesList.replaceChildren();
  replies.forEach(({ id, data }) => {
    repliesList.appendChild(renderReply(state, commentId, id, data));
  });
}

function isOwnMessage(state, msg) {
  return Boolean(msg.uid && state.currentUser && msg.uid === state.currentUser.uid);
}

// The "Academy Team" badge is derived at render time from the configured admin
// list (adminUids) keyed on the author's Firebase uid — never from a stored
// `isAdmin` flag, which any client could forge when writing a doc directly to
// Firestore with the public web key.
function isAdminAuthor(state, item) {
  return Boolean(item && item.uid && state.config.adminUids.has(item.uid));
}

function toggleReplies(state, commentId, repliesEl, repliesList) {
  let entry = state.detail.comments.get(commentId);
  if (!entry) {
    entry = { expanded: false, replies: [], unsubscribe: null };
    state.detail.comments.set(commentId, entry);
  }

  entry.expanded = !entry.expanded;
  repliesEl.hidden = !entry.expanded;

  if (!entry.unsubscribe) {
    const { collection, query, orderBy, onSnapshot } = state.firestore;
    const q = query(
      collection(state.db, 'communityPosts', state.detail.postId, 'comments', commentId, 'replies'),
      orderBy('timestamp', 'asc')
    );
    entry.unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        entry.replies = [];
        snapshot.forEach((doc) => entry.replies.push({ id: doc.id, data: doc.data() }));
        if (entry.expanded) {
          renderReplies(state, commentId, repliesList, entry.replies);
        }
      },
      (err) => {
        console.error('[lwes-community] replies onSnapshot error', err);
      }
    );
  } else if (entry.expanded) {
    renderReplies(state, commentId, repliesList, entry.replies);
  }

  return entry.expanded;
}

// ---- Comment / reply composers ----

function setupCommentForm(state) {
  const image = buildCommentImagePicker(state);
  if (image && state.refs.commentStatus) {
    state.refs.commentForm.insertBefore(image.wrap, state.refs.commentStatus);
  }
  attachCommentSubmit(
    state,
    {
      form: state.refs.commentForm,
      message: state.refs.commentInput,
      status: state.refs.commentStatus,
      image,
    },
    null
  );
}

// A compact "Add photo" picker for comment and reply composers — the same
// imgbb-backed flow the post composer uses, built in JS so it can be dropped
// into either form. Returns null when photo uploads aren't configured.
function buildCommentImagePicker(state) {
  if (!state.config.imgbbApiKey) return null;

  let file = null;
  let objectUrl = null;

  const wrap = document.createElement('div');
  wrap.className = 'lc-comment-image';

  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.className = 'lc-comment-image__input';
  input.hidden = true;

  const addBtn = document.createElement('button');
  addBtn.type = 'button';
  addBtn.className = 'lc-comment-image__add';
  addBtn.append(cameraIcon());
  const addText = document.createElement('span');
  addText.textContent = state.config.addPhotoLabel;
  addBtn.appendChild(addText);
  addBtn.addEventListener('click', () => input.click());

  const preview = document.createElement('div');
  preview.className = 'lc-comment-image__preview';
  preview.hidden = true;
  const img = document.createElement('img');
  const removeBtn = document.createElement('button');
  removeBtn.type = 'button';
  removeBtn.className = 'lc-comment-image__remove';
  removeBtn.setAttribute('aria-label', state.config.removePhotoLabel);
  removeBtn.textContent = '×';
  preview.append(img, removeBtn);

  const clear = () => {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    objectUrl = null;
    file = null;
    img.removeAttribute('src');
    preview.hidden = true;
    input.value = '';
    addBtn.hidden = false;
  };

  input.addEventListener('change', () => {
    const f = input.files && input.files[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      window.alert('Please choose an image file.');
      input.value = '';
      return;
    }
    if (f.size > state.config.maxImageMb * 1024 * 1024) {
      window.alert(`Photo is too large — max ${state.config.maxImageMb} MB.`);
      input.value = '';
      return;
    }
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    file = f;
    objectUrl = URL.createObjectURL(f);
    img.src = objectUrl;
    preview.hidden = false;
    addBtn.hidden = true;
  });

  removeBtn.addEventListener('click', clear);

  wrap.append(addBtn, input, preview);
  return { wrap, getFile: () => file, clear };
}

function cameraIcon() {
  const NS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  const body = document.createElementNS(NS, 'path');
  body.setAttribute('d', 'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z');
  const lens = document.createElementNS(NS, 'circle');
  lens.setAttribute('cx', '12');
  lens.setAttribute('cy', '13');
  lens.setAttribute('r', '4');
  svg.append(body, lens);
  return svg;
}

function buildReplyComposer(state, commentId) {
  const wrap = document.createElement('div');
  wrap.className = 'lc-comment__composer';

  const loginEl = document.createElement('div');
  loginEl.className = 'lc-login lc-login--link';

  // Compact panel (provider buttons only). Once signed in anywhere — including
  // the post composer's email form — every composer updates via onAuthChanged.
  const authui = document.createElement('div');
  authui.className = 'lc-authui';
  renderAuthPanel(state, authui, true);
  loginEl.appendChild(authui);

  const formEl = document.createElement('form');
  formEl.className = 'lc-comment-form lc-comment-form--reply';
  formEl.noValidate = true;

  const posterEl = document.createElement('div');
  posterEl.className = 'lc-poster lc-poster--sm';

  const row = document.createElement('div');
  row.className = 'lc-comment-form__row';

  const textarea = document.createElement('textarea');
  textarea.className = 'lc-comment-form__input lc-comment-form__input--reply';
  textarea.rows = 1;
  textarea.maxLength = COMMENT_MAX;
  textarea.placeholder = state.config.replyPlaceholder;
  textarea.required = true;

  const sendBtn = document.createElement('button');
  sendBtn.type = 'submit';
  sendBtn.className = 'lc-btn lc-btn--green lc-comment-form__send';
  sendBtn.textContent = state.config.sendLabel;

  row.append(textarea, sendBtn);

  const status = document.createElement('p');
  status.className = 'lc-status';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');

  const image = buildCommentImagePicker(state);

  formEl.append(posterEl, row);
  if (image) formEl.appendChild(image.wrap);
  formEl.appendChild(status);
  wrap.append(loginEl, formEl);

  attachCommentSubmit(state, { form: formEl, message: textarea, status, image }, commentId);

  if (state.currentUser) {
    loginEl.hidden = true;
    formEl.hidden = false;
    renderPoster(state, posterEl);
  } else {
    loginEl.hidden = false;
    formEl.hidden = true;
  }

  return wrap;
}

// commentId === null → top-level comment on the open post;
// otherwise a reply under that comment.
function attachCommentSubmit(state, refs, commentId) {
  refs.form.addEventListener('submit', async (e) => {
    e.preventDefault();
    setStatus(refs.status, '', null);

    if (!state.currentUser) {
      setStatus(refs.status, 'Please sign in first.', 'error');
      return;
    }
    const postId = state.detail.postId;
    if (!postId) return;

    const message = (refs.message.value || '').trim().slice(0, COMMENT_MAX);
    if (!message) {
      setStatus(refs.status, 'Please enter a message.', 'error');
      return;
    }

    const remaining = rateLimitRemaining(STORAGE_KEYS.lastComment, COMMENT_RATE_LIMIT_MS);
    if (remaining > 0) {
      setStatus(refs.status, `Please wait ${remaining}s before sending another message.`, 'error');
      return;
    }

    const imageFile = refs.image ? refs.image.getFile() : null;

    const { collection, addDoc, doc, updateDoc, increment, serverTimestamp } = state.firestore;
    const payload = {
      name: state.currentUser.name,
      message,
      photoURL: state.currentUser.photoURL,
      uid: state.currentUser.uid,
      timestamp: serverTimestamp(),
    };

    setFormDisabled(refs.form, true);
    try {
      if (imageFile) {
        setStatus(refs.status, 'Uploading photo…', null);
        payload.imageURL = (await uploadToImgbb(state.config.imgbbApiKey, imageFile)).slice(0, 1000);
      }

      const target = commentId
        ? collection(state.db, 'communityPosts', postId, 'comments', commentId, 'replies')
        : collection(state.db, 'communityPosts', postId, 'comments');
      await addDoc(target, payload);

      if (commentId) {
        const parentRef = doc(state.db, 'communityPosts', postId, 'comments', commentId);
        updateDoc(parentRef, { replyCount: increment(1) }).catch((err) => {
          console.error('[lwes-community] reply count update failed', err);
        });
      }
      // commentCount on the post tracks the whole discussion (comments +
      // replies), incremented +1 per write — same pattern as replyCount.
      const postRef = doc(state.db, 'communityPosts', postId);
      updateDoc(postRef, { commentCount: increment(1) }).catch((err) => {
        console.error('[lwes-community] comment count update failed', err);
      });

      refs.message.value = '';
      if (refs.image) refs.image.clear();
      localStorage.setItem(STORAGE_KEYS.lastComment, String(Date.now()));
      setStatus(refs.status, 'Sent!', 'success');
    } catch (err) {
      console.error('[lwes-community] addDoc failed', err);
      setStatus(refs.status, 'Could not send your message. Please try again.', 'error');
    } finally {
      setFormDisabled(refs.form, false);
      refs.message.focus();
    }
  });
}

// ---- Shared form helpers (copied from lwes-academy-chat.js) ----

function rateLimitRemaining(storageKey, windowMs) {
  const last = Number(localStorage.getItem(storageKey) || 0);
  const elapsed = Date.now() - last;
  return elapsed >= windowMs ? 0 : Math.ceil((windowMs - elapsed) / 1000);
}

function setFormDisabled(formEl, disabled) {
  formEl.querySelectorAll('input, textarea, select, button').forEach((el) => {
    el.disabled = disabled;
  });
}

function setStatus(statusEl, msg, type) {
  if (!statusEl) return;
  statusEl.textContent = msg;
  statusEl.classList.toggle('is-error', type === 'error');
  statusEl.classList.toggle('is-success', type === 'success');
}
